import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import MoodOrb from '../components/MoodOrb';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PlusCircle, TrendingUp, Calendar, BookOpen, Smile, Flame, Trophy, Star, Crown, Medal, Gem, Sparkles, Zap, Heart, RefreshCw } from 'lucide-react';

const BADGE_ICONS = {
    seedling: Zap,
    flame: Flame,
    star: Star,
    trophy: Trophy,
    medal: Medal,
    crown: Crown,
    gem: Gem,
    sparkles: Sparkles
};

const Dashboard = () => {
    const { user, api } = useAuth();
    const [stats, setStats] = useState(null);
    const [recentEntries, setRecentEntries] = useState([]);
    const [affirmation, setAffirmation] = useState(null);
    const [loadingAffirmation, setLoadingAffirmation] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
        fetchAffirmation();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, entriesRes] = await Promise.all([
                api.get('/journal/stats?days=30'),
                api.get('/journal/entries?limit=5')
            ]);
            setStats(statsRes.data);
            setRecentEntries(entriesRes.data);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAffirmation = async () => {
        setLoadingAffirmation(true);
        try {
            const response = await api.get('/ai/affirmation');
            setAffirmation(response.data);
        } catch (error) {
            console.error('Failed to fetch affirmation:', error);
        } finally {
            setLoadingAffirmation(false);
        }
    };

    const latestMood = recentEntries[0]?.mood_rating || 5;
    const latestEmoji = recentEntries[0]?.emoji || '😊';
    const moodStreak = stats?.mood_streak || user?.mood_streak || 0;
    const badges = stats?.badges || user?.badges || [];
    const nextBadge = stats?.next_badge;

    return (
        <DashboardLayout>
            <div data-testid="dashboard" className="space-y-8">
                {/* Welcome Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                >
                    <div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground">
                            Welcome back, {user?.name?.split(' ')[0]}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            How are you feeling today?
                        </p>
                    </div>
                    <Link to="/new-entry">
                        <Button 
                            data-testid="new-entry-btn"
                            className="rounded-full px-6 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                        >
                            <PlusCircle className="mr-2 w-5 h-5" />
                            New Entry
                        </Button>
                    </Link>
                </motion.div>

                {/* Mood Streak Card */}
                {moodStreak > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                    >
                        <Card className="glass-card bg-gradient-to-r from-chart-4/10 to-chart-1/10 border-chart-4/30" data-testid="mood-streak-card">
                            <CardContent className="p-6">
                                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 bg-chart-4/20 rounded-2xl flex items-center justify-center">
                                            <Flame className="w-8 h-8 text-chart-4" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Current Streak</p>
                                            <p className="font-heading text-3xl font-semibold text-foreground" data-testid="streak-count">
                                                {moodStreak} day{moodStreak !== 1 ? 's' : ''}
                                            </p>
                                            {nextBadge && (
                                                <p className="text-sm text-chart-4 mt-1">
                                                    {nextBadge.days_remaining} day{nextBadge.days_remaining !== 1 ? 's' : ''} until &quot;{nextBadge.badge?.name}&quot;
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Earned Badges */}
                                    {badges.length > 0 && (
                                        <div className="flex flex-wrap gap-2" data-testid="earned-badges">
                                            {badges.map((badge, index) => {
                                                const IconComponent = BADGE_ICONS[badge.icon] || Star;
                                                return (
                                                    <motion.div
                                                        key={badge.name}
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        transition={{ delay: index * 0.1 }}
                                                        className="flex items-center gap-2 px-3 py-2 rounded-full bg-background/50 border border-chart-4/30"
                                                        title={badge.description}
                                                    >
                                                        <IconComponent className="w-4 h-4 text-chart-4" />
                                                        <span className="text-sm font-medium">{badge.name}</span>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Daily Affirmation */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <Card className="glass-card bg-gradient-to-br from-primary/5 to-accent/5" data-testid="affirmation-card">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                        <Heart className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium">Today&apos;s Affirmation</h3>
                                        {affirmation?.personalized && (
                                            <span className="text-xs text-primary">Personalized for you</span>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={fetchAffirmation}
                                    disabled={loadingAffirmation}
                                    data-testid="refresh-affirmation-btn"
                                >
                                    <RefreshCw className={`w-4 h-4 ${loadingAffirmation ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                            <p className="text-lg font-heading italic text-foreground mt-4">
                                &quot;{affirmation?.affirmation || 'Loading your daily affirmation...'}&quot;
                            </p>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Bento Grid */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {/* Mood Orb Card - Large */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                        className="md:col-span-5"
                    >
                        <Card className="glass-card h-full">
                            <CardHeader>
                                <CardTitle className="font-heading text-xl">Your Mood Aura</CardTitle>
                            </CardHeader>
                            <CardContent className="flex flex-col items-center justify-center py-8">
                                <MoodOrb moodRating={latestMood} emoji={latestEmoji} size="lg" />
                                <p className="mt-6 text-muted-foreground text-center">
                                    {recentEntries.length > 0 
                                        ? `Last entry: ${new Date(recentEntries[0].created_at).toLocaleDateString()}`
                                        : 'No entries yet. Start your journey!'}
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Stats Cards */}
                    <div className="md:col-span-7 grid grid-cols-2 gap-6">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <Card className="glass-card h-full">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 bg-chart-1/20 rounded-xl flex items-center justify-center">
                                            <TrendingUp className="w-5 h-5 text-chart-1" />
                                        </div>
                                        <span className="text-muted-foreground">Avg Mood</span>
                                    </div>
                                    <p className="font-heading text-4xl font-semibold text-foreground">
                                        {stats?.average_mood || '—'}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">Last 30 days</p>
                                </CardContent>
                            </Card>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <Card className="glass-card h-full">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 bg-chart-2/20 rounded-xl flex items-center justify-center">
                                            <BookOpen className="w-5 h-5 text-chart-2" />
                                        </div>
                                        <span className="text-muted-foreground">Entries</span>
                                    </div>
                                    <p className="font-heading text-4xl font-semibold text-foreground">
                                        {stats?.total_entries || 0}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">Total written</p>
                                </CardContent>
                            </Card>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="col-span-2"
                        >
                            <Card className="glass-card">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 bg-chart-3/20 rounded-xl flex items-center justify-center">
                                            <Smile className="w-5 h-5 text-chart-3" />
                                        </div>
                                        <span className="text-muted-foreground">Top Emojis</span>
                                    </div>
                                    <div className="flex gap-4 text-3xl">
                                        {stats?.emoji_frequency 
                                            ? Object.entries(stats.emoji_frequency)
                                                .sort((a, b) => b[1] - a[1])
                                                .slice(0, 5)
                                                .map(([emoji, count]) => (
                                                    <span key={emoji} title={`Used ${count} times`}>
                                                        {emoji}
                                                    </span>
                                                ))
                                            : <span className="text-muted-foreground text-base">No data yet</span>
                                        }
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>

                    {/* Recent Entries */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="md:col-span-12"
                    >
                        <Card className="glass-card">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-heading text-xl">Recent Entries</CardTitle>
                                <Link to="/entries">
                                    <Button variant="ghost" className="text-primary">
                                        View All
                                    </Button>
                                </Link>
                            </CardHeader>
                            <CardContent>
                                {recentEntries.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                        <p className="text-muted-foreground">No entries yet</p>
                                        <Link to="/new-entry">
                                            <Button className="mt-4 rounded-full" data-testid="create-first-entry-btn">
                                                Create Your First Entry
                                            </Button>
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {recentEntries.map((entry) => (
                                            <Link 
                                                key={entry.id} 
                                                to={`/entries/${entry.id}`}
                                                className="block"
                                            >
                                                <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
                                                    <div className="text-3xl">{entry.emoji}</div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-foreground font-medium truncate">
                                                            {entry.content.substring(0, 100)}...
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {new Date(entry.created_at).toLocaleDateString('en-US', {
                                                                weekday: 'short',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                            {entry.location_name && ` • ${entry.location_name}`}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium bg-chart-${Math.min(5, Math.ceil(entry.mood_rating / 2))}/20 text-chart-${Math.min(5, Math.ceil(entry.mood_rating / 2))}`}>
                                                            {entry.mood_rating}/10
                                                        </span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Dashboard;
